import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { pushSupported, wirePushEventListeners } from '@pp/shared/push/push';

/**
 * Listens for `push:tapped` window events (fired by the wiring in
 * `utils/pushNotifications.ts`) and routes the user to the deep link
 * carried in the notification's data payload.
 *
 * The Capacitor push plugin surfaces a `pushNotificationActionPerformed`
 * event when the user taps a system notification — either to launch the
 * app from a killed/background state or to bring it back to the
 * foreground. The custom `data` we put in the APNs / FCM payload server-
 * side lands on `notification.data` here; we treat `data.deepLink` as
 * an in-app react-router path.
 *
 * Mount once inside the BrowserRouter (so `useNavigate` works) and
 * outside any route element (so taps received while on any screen still
 * route correctly).
 */
export function PushTapHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    // Install the underlying Capacitor plugin listeners at app start so
    // a tap that launches the app from killed state can fire even
    // before the user signs in. enablePush() installs them too, but
    // the cold-launch path through /unlock or /login runs first.
    // No-op on web; pushSupported() gates the call.
    if (pushSupported()) {
      wirePushEventListeners();
    }

    const onTap = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { notification?: { data?: Record<string, unknown> } }
        | undefined;
      const deepLink = detail?.notification?.data?.deepLink;

      // Defense-in-depth: only follow same-origin in-app paths. A push
      // payload arrives from our own server, but treating it as
      // untrusted input means a misconfigured event trigger can't
      // bounce users off-site or onto an absolute URL we didn't mean.
      if (typeof deepLink !== 'string') return;
      if (!deepLink.startsWith('/') || deepLink.startsWith('//')) return;

      navigate(deepLink);
    };

    window.addEventListener('push:tapped', onTap);
    return () => window.removeEventListener('push:tapped', onTap);
  }, [navigate]);

  return null;
}
